import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Check, ShieldCheck, FileText, Truck, PackageCheck } from "lucide-react";
import Reveal from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { mediaUrl } from "../api";
import { useRFQ } from "../context/RFQContext";
import { Button } from "../components/ui/button";

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Regulatory support", desc: "Dossier and compliance handled with accredited partners." },
  { icon: FileText, title: "Export documentation", desc: "Complete paperwork for smooth global exports." },
  { icon: PackageCheck, title: "Pre-qualified supply", desc: "Sourced from vetted manufacturing partners." },
  { icon: Truck, title: "Reliable logistics", desc: "Supply chain coordination from start to finish." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToEnquiry, isInEnquiry, openRFQ } = useRFQ();
  const { products, content, loading } = useContent();
  const product = products.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="text-muted-foreground">{loading ? "Loading..." : "Product not found."}</p>
        <Link to="/products" className="mt-4 inline-block text-[#2ED9B8] font-medium">Back to products</Link>
      </div>
    );
  }

  const productImage = mediaUrl(product.image || (content.images || {}).blister);

  const added = isInEnquiry(product.slug);
  const categoryName = product.group === "Dosage Forms" ? "Dosage Forms" : product.category || product.group;
  const related = products.filter((p) => (p.group === "Dosage Forms" ? "Dosage Forms" : p.category || p.group) === categoryName && p.slug !== product.slug).slice(0, 3);

  return (
    <>
      <section className="pt-32 lg:pt-40 pb-16 mint-gradient">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="rounded-[22px] overflow-hidden ring-1 ring-black/5 shadow-[0_30px_70px_-30px_rgba(12,97,82,0.4)]">
                <img src={productImage} alt={product.name} className="w-full h-[360px] object-cover" />
              </div>
            </Reveal>
            <div>
              <span className="inline-block text-[11px] uppercase tracking-[0.15em] text-[#2ED9B8] bg-card border border-white/10 px-3 py-1.5 rounded-full">
                {categoryName}
              </span>
              <h1 className="mt-4 font-display text-[38px] lg:text-[48px] leading-[1.05] text-foreground">{product.name}</h1>
              <p className="mt-5 text-[16px] leading-relaxed text-muted-foreground max-w-xl">{product.blurb}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={openRFQ} className="bg-[#00ab96] hover:bg-[#0b8f7e] text-white rounded-full h-12 px-7">
                  Enquire about this product
                </Button>
                <button
                  onClick={() => addToEnquiry({ slug: product.slug, name: product.name })}
                  disabled={added}
                  className={`inline-flex items-center gap-2 rounded-full h-12 px-6 border font-medium transition-colors ${
                    added ? "bg-[#0f2236] border-white/10 text-[#2ED9B8]" : "bg-card border-border text-foreground hover:border-[#2ED9B8]"
                  }`}
                >
                  {added ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {added ? "Added to enquiry" : "Add to enquiry list"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <button onClick={() => navigate(-1)} className="tap-target inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#2ED9B8] mb-10">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={(i % 4) * 70}>
                <div className="h-full bg-card rounded-2xl border border-border p-6">
                  <h.icon className="h-6 w-6 text-[#2ED9B8]" strokeWidth={1.8} />
                  <h3 className="mt-4 font-display text-[18px] text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="font-display text-[26px] text-foreground">Related in {categoryName}</h2>
              <div className="mt-6 grid sm:grid-cols-3 gap-5">
                {related.map((r) => (
                  <Link key={r.slug} to={`/products/${r.slug}`} className="group bg-card rounded-2xl border border-white/10 p-6 hover:border-[#2ED9B8]/25 transition-colors">
                    <h3 className="font-display text-[19px] text-foreground">{r.name}</h3>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#2ED9B8] font-medium">View details</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

    </>
  );
}
